'use client';

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Send, CheckCircle, Sparkles } from 'lucide-react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { ToastContainer } from '@/components/ui/Toast';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

const faqs = [
  {
    q: 'Is Stranger World completely anonymous?',
    a: 'Yes — anonymous users leave zero personal information. We create a temporary session token for matching, but no name, email, or location is required or stored.',
  },
  {
    q: 'How does the gender filter work?',
    a: 'You can choose to specifically chat with female strangers. Free accounts get 10 matches per month. Premium users will get unlimited gender-filtered matches — Premium is coming soon.',
  },
  {
    q: 'What happens when I click "Next"?',
    a: 'The current chat session ends for both parties, and you are both placed back in the matching queue to find new strangers.',
  },
  {
    q: 'How do I report abusive behavior?',
    a: 'Click the Flag icon inside any active chat. Select a reason and optionally add details. Our moderation team reviews reports within 24 hours.',
  },
  {
    q: 'Are my messages stored?',
    a: 'For anonymous users, messages are ephemeral — they are not persisted after the session ends. Flagged messages may be retained for moderation review.',
  },
  {
    q: 'How do I cancel my Premium subscription?',
    a: 'Premium subscriptions are coming soon. Once launched, you will be able to manage billing from your account settings and cancel anytime.',
  },
  {
    q: 'Why was my account banned?',
    a: 'Accounts are banned for policy violations including harassment, sharing explicit content, or spam. Contact support if you believe your ban was in error.',
  },
  {
    q: 'Is Stranger World safe for minors?',
    a: 'Stranger World is intended for users aged 18 and above only. We have moderation in place, but recommend parental supervision for younger users regardless.',
  },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/[0.08] rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-brand-surface-2/50 transition-colors"
      >
        <span className="text-sm font-medium text-brand-white">{q}</span>
        {open ? (
          <ChevronUp size={16} className="text-brand-lime shrink-0" />
        ) : (
          <ChevronDown size={16} className="text-brand-gray shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-4 border-t border-white/[0.06]">
          <p className="text-sm text-brand-gray leading-relaxed pt-4">{a}</p>
        </div>
      )}
    </div>
  );
}

export default function SupportPage() {
  const [form, setForm] = useState({ email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [ticketId, setTicketId] = useState('');
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await api.support.createTicket(form) as { ticket: { id: string } };
      setTicketId(data.ticket.id);
      setForm({ email: '', subject: '', message: '' });
    } catch {
      setError('Failed to submit ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Navbar />

      {/* Premium coming soon banner */}
      <div className="border-b border-brand-lime/20 bg-brand-lime/[0.04] mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-center gap-3">
          <Sparkles size={14} className="text-brand-lime shrink-0" />
          <p className="text-sm text-brand-gray-light">
            <span className="text-brand-white font-medium">Premium subscription coming soon.</span>
            {' '}Unlock unlimited gender filters, priority support, and exclusive features.
          </p>
        </div>
      </div>

      <div className="min-h-screen bg-brand-black pb-20 md:pb-0">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-bold text-brand-white mb-4">
              Help & <span className="gradient-lime-text">Support</span>
            </h1>
            <p className="text-brand-gray-light">
              Find answers to common questions or get in touch with our team.
            </p>
          </div>

          {/* FAQ Section */}
          <section id="faq" className="mb-20">
            <h2 className="text-2xl font-bold text-brand-white mb-8">
              Frequently Asked Questions
            </h2>
            <div className="flex flex-col gap-3">
              {faqs.map(({ q, a }) => (
                <FaqItem key={q} q={q} a={a} />
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section id="contact">
            <div className="bg-surface-elevated rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-brand-white mb-2">Contact Support</h2>
              <p className="text-sm text-brand-gray mb-8">
                Can&apos;t find an answer? Submit a ticket and we&apos;ll get back to you within 24 hours.
              </p>

              {ticketId ? (
                <div className="text-center py-8">
                  <CheckCircle size={40} className="text-brand-lime mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-brand-white mb-2">Ticket Submitted!</h3>
                  <p className="text-sm text-brand-gray mb-2">Your ticket ID:</p>
                  <p className="font-mono text-xs text-brand-lime bg-brand-lime/10 px-3 py-1.5 rounded-lg inline-block">
                    {ticketId}
                  </p>
                  <p className="text-xs text-brand-gray mt-4">We&apos;ll respond directly to your email address within 24 hours.</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-6"
                    onClick={() => setTicketId('')}
                  >
                    Submit Another
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <Input
                    label="Email"
                    type="email"
                    placeholder="your@email.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                  />
                  <Input
                    label="Subject"
                    type="text"
                    placeholder="Briefly describe your issue"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    required
                  />
                  <Textarea
                    label="Message"
                    placeholder="Describe your issue in detail. Include your ticket ID if you have one."
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    required
                    rows={5}
                  />
                  {error && (
                    <p className="text-sm text-brand-red">{error}</p>
                  )}
                  <Button type="submit" loading={loading} className="self-start">
                    <Send size={14} />
                    Send Message
                  </Button>
                </form>
              )}
            </div>
          </section>
        </div>
      </div>
      <Footer />
      <ToastContainer />
    </>
  );
}
